<!-- components/pages/Documents/Document.svelte -->
<script>
  import { navigate } from "svelte-routing";
  import { onMount, afterUpdate, tick } from "svelte";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import Button from "lib/components/Button.svelte";
  import BackToTop from "lib/components/BackToTop.svelte";
  import Spinner from "lib/components/Spinner.svelte";
  import DocumentMenu from "lib/components/DocumentMenu.svelte";
  import {
    getPaginator,
    scrollToSection,
    scrollToTop,
    loadTOC,
    parseAnchors,
    getParsedPageData,
    getParsedURLParams,
  } from "lib/modules/Document.svelte";

  export let params;
  export let defaultSubPage;
  export let baseDocumentUrl;
  export let menuTitle;
  export let documentVersion;
  export let documentSourceUrl;

  let previousPage;
  let activeSubPage;
  let activeSection;
  let loading = false;
  let parsedTOC = {};
  let pageData = "";
  let currentPaginator;
  let mobileMenuOpen = false;

  $: {
    currentPaginator = getPaginator(activeSubPage, parsedTOC);
  }

  const parseURLParams = () => {
    [activeSubPage, activeSection] = getParsedURLParams(params, defaultSubPage);
    navigateToPage(activeSubPage, activeSection);
  };

  const sanitizePage = (page) => {
    if (page && page.endsWith("/")) {
      page = page.slice(0, -1);
    }
    return page;
  };

  const navigateToPage = async (page, section) => {
    loading = true;
    page = sanitizePage(page);
    activeSubPage = page;
    await loadPageData(page);
    loading = false;
    scrollToSection(section, tick);

    // Close mobile menu when navigating
    mobileMenuOpen = false;

    // If no section is specified, scroll to top by default
    if (!section) {
      scrollToTop();
    }
  };

  const loadPageData = async (page) => {
    // Do not reload the same page
    if (page === previousPage) {
      return;
    }
    previousPage = page;
    pageData = ""; // Clear (and rerender) previous page before loading new one

    try {
      pageData = await getParsedPageData(page, documentSourceUrl);

      // Check if pageData is empty or null after loading
      if (!pageData || pageData.trim() === "") {
        console.warn(`Failed to load page data for: ${page}`);
        window.location.href = "/";
        return;
      }
    } catch (error) {
      console.error(`Error loading page data for: ${page}`, error);
      window.location.href = "/";
      return;
    }
  };

  const initSubPage = async () => {
    parseURLParams();
    parsedTOC = await loadTOC(documentSourceUrl);
    navigateToPage(activeSubPage, activeSection);
  };

  const onHashChange = () => {
    parseURLParams();
  };

  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
  }

  onMount(() => initSubPage(documentSourceUrl));
  afterUpdate(() => {
    parseAnchors(baseDocumentUrl, activeSubPage, navigateToPage);
  });

  // Reload dynamic page contents upon all navigation (incl. back button)
  window.onhashchange = onHashChange;

  // The legal docs are shown in an embedded web view inside the
  // mobile and desktop apps – where we don't want to show any
  // navigation elements, just the formatted document content
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbedded = urlParams.has("embedded");
</script>

<svelte:head>
  <title>{menuTitle} - Liquid Noble</title>
  <meta name="description" content="Liquid Noble {menuTitle}" />
</svelte:head>

{#if isEmbedded}
  <!-- Embedded view for mobile/desktop apps -->
  <div class="embedded-document">
    {#if !pageData || loading}
      <div class="loading-container">
        <Spinner size="medium" variant="dark" label="Loading document..." />
      </div>
    {:else}
      <div class="embedded-content">
        {@html pageData}
      </div>
    {/if}
  </div>
{:else}
  <!-- Full website view -->
  <Header />

  <Container variant="dark" size="large" class="document-container">
    <div class="document-layout">
      <!-- Mobile Menu Toggle -->
      <div class="mobile-menu-header">
        <h1 class="document-title">{menuTitle}</h1>
        <button
          class="mobile-menu-toggle"
          class:open={mobileMenuOpen}
          on:click={toggleMobileMenu}
          aria-label="Toggle document menu"
        >
          <span class="menu-icon"></span>
          <span class="menu-icon"></span>
          <span class="menu-icon"></span>
        </button>
      </div>

      {#if !pageData || loading}
        <div class="loading-container">
          <Spinner size="large" variant="gold" label="Loading document..." />
        </div>
      {:else}
        <div class="document-content">
          <!-- Desktop Sidebar -->
          <aside class="sidebar desktop-only">
            <div class="sidebar-content">
              <h2 class="sidebar-title">{menuTitle}</h2>
              <DocumentMenu
                bind:parsedTOC
                bind:activeSubPage
                {navigateToPage}
                {baseDocumentUrl}
                {documentVersion}
                {menuTitle}
              />
            </div>
          </aside>

          <!-- Mobile Sidebar Overlay -->
          <aside class="sidebar mobile-overlay" class:open={mobileMenuOpen}>
            <div
              class="overlay-backdrop"
              on:click={() => (mobileMenuOpen = false)}
            ></div>
            <div class="sidebar-content">
              <div class="mobile-menu-header-inner">
                <h2 class="sidebar-title">{menuTitle}</h2>
                <button
                  class="close-button"
                  on:click={() => (mobileMenuOpen = false)}
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>
              <DocumentMenu
                bind:parsedTOC
                bind:activeSubPage
                {navigateToPage}
                {baseDocumentUrl}
                {documentVersion}
                {menuTitle}
                mobile
              />
            </div>
          </aside>

          <!-- Main Content Area -->
          <main class="main-content">
            <article class="document-article">
              {@html pageData}
            </article>

            <!-- Pagination -->
            {#if currentPaginator}
              <nav class="pagination" aria-label="Document navigation">
                <div class="pagination-button previous">
                  {#if currentPaginator.previousLink}
                    <Button
                      variant="secondary"
                      size="medium"
                      responsive={true}
                      fullWidth={true}
                      noWrap={true}
                      on:click={() => {
                        const newUrl = `${baseDocumentUrl}${currentPaginator.previousLink}`;
                        navigateToPage(currentPaginator.previousLink);
                        navigate(newUrl, { replace: true });
                      }}
                    >
                      ← {currentPaginator.previousLabel}
                    </Button>
                  {/if}
                </div>
                <div class="pagination-button next">
                  {#if currentPaginator.nextLink}
                    <Button
                      variant="primary"
                      size="medium"
                      responsive={true}
                      fullWidth={true}
                      noWrap={true}
                      on:click={() => {
                        const newUrl = `${baseDocumentUrl}${currentPaginator.nextLink}`;
                        navigateToPage(currentPaginator.nextLink);
                        navigate(newUrl, { replace: true });
                      }}
                    >
                      {currentPaginator.nextLabel} →
                    </Button>
                  {/if}
                </div>
              </nav>
            {/if}
          </main>
        </div>
      {/if}
    </div>
  </Container>

  <BackToTop />
  <Footer />
{/if}

<style lang="scss">
  .document-layout {
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
  }

  .mobile-menu-header {
    display: none;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 1rem;

    @media (max-width: 1024px) {
      display: flex;
    }

    @media (max-width: 480px) {
      padding: 0.5rem 0 0.75rem;
      margin-bottom: 0.75rem;
    }
  }

  .document-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--text-color1);
    margin: 0;

    @media (max-width: 768px) {
      font-size: 1.75rem;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  .mobile-menu-toggle {
    display: flex;
    flex-direction: column;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    gap: 0.25rem;
    z-index: 99; // Lower than header mobile nav (500) but higher than header base (100)

    &.open .menu-icon:nth-child(1) {
      transform: rotate(45deg) translate(6px, 6px);
    }

    &.open .menu-icon:nth-child(2) {
      opacity: 0;
    }

    &.open .menu-icon:nth-child(3) {
      transform: rotate(-45deg) translate(6px, -6px);
    }
  }

  .menu-icon {
    width: 24px;
    height: 3px;
    background-color: var(--text-color1);
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
  }

  .document-content {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 4rem;
    align-items: start;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }

  .sidebar {
    position: sticky;
    top: 2rem;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;

    &.desktop-only {
      @media (max-width: 1024px) {
        display: none;
      }
    }

    &.mobile-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      min-height: 100vh;
      z-index: 1000;
      overflow-y: auto;

      @media (max-width: 1024px) {
        &.open {
          display: block;
        }
      }
    }
  }

  .overlay-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    min-height: 100vh;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(4px);
  }

  .sidebar-content {
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 2rem;
    position: relative;
    z-index: 1001;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;

    /* Custom scrollbar for better UX */
    scrollbar-width: thin;
    scrollbar-color: rgba(210, 165, 82, 0.3) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(210, 165, 82, 0.3);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(210, 165, 82, 0.5);
    }

    .mobile-overlay & {
      margin: 0;
      width: 100%;
      height: 100vh;
      min-height: 100vh;
      max-width: none;
      max-height: none;
      margin-top: 0;
      padding: 2rem;
      background: rgba(255, 255, 255, 0.1);
      border: none;
      border-radius: 0;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;

      @media (max-width: 768px) {
        padding: 1.5rem;
      }

      @media (max-width: 480px) {
        padding: 1rem;
      }
    }
  }

  .mobile-menu-header-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .close-button {
    background: none;
    border: none;
    color: var(--text-color1);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;

    &:hover {
      opacity: 0.7;
    }
  }

  .sidebar-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-color1);
    margin: 0 0 1.5rem 0;
  }

  .main-content {
    min-width: 0; // Prevents overflow in grid
    overflow-x: auto;
  }

  .document-article {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 3rem;
    margin-bottom: 3rem;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      padding: 1rem 2rem;
    }

    @media (max-width: 480px) {
      padding: 1rem 1.5rem;
    }

    // Document content styling with better contrast
    :global(h1) {
      color: var(--text-color1);
      font-size: 3rem;
      font-weight: 700;
      line-height: 1.2;
      margin: 0 0 2rem 0;

      @media (max-width: 768px) {
        font-size: 2.5rem;
      }

      @media (max-width: 480px) {
        font-size: 2rem;
      }
    }

    :global(h2) {
      color: var(--text-color1);
      font-size: 2.25rem;
      font-weight: 600;
      line-height: 1.3;
      margin: 3rem 0 1.5rem 0;

      @media (max-width: 768px) {
        font-size: 2rem;
        margin: 1.5rem 0 1.5rem;
      }

      @media (max-width: 480px) {
        font-size: 1.75rem;
        margin: 1.5rem 0 1.5rem;
      }
    }

    :global(h3) {
      color: var(--text-color1);
      font-size: 1.5rem;
      font-weight: 600;
      line-height: 1.4;
      margin: 2rem 0 1rem 0;
    }

    :global(h4) {
      color: var(--text-color1);
      font-size: 1.25rem;
      font-weight: 600;
      line-height: 1.4;
      margin: 1.5rem 0 0.75rem 0;
    }

    :global(h5) {
      color: var(--text-color1);
      font-size: 1.125rem;
      font-weight: 500;
      line-height: 1.4;
      margin: 1rem 0 0.5rem 0;
    }

    :global(h6) {
      color: var(--text-color2);
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.4;
      margin: 1rem 0 0.5rem 0;
    }

    :global(p) {
      color: var(--text-color2);
      font-size: 1rem;
      line-height: 1.6;
      margin: 0 0 1.5rem 0;
    }

    :global(ul),
    :global(ol) {
      color: var(--text-color2);
      margin: 0 0 1.5rem 0;
      padding-left: 2rem;
    }

    :global(li) {
      margin: 0.5rem 0;
      line-height: 1.6;
      color: var(--text-color2);
    }

    // Flags list styling fix
    :global(.flags) {
      list-style: none;
      padding-left: 0;
      margin: 1.5rem 0;

      :global(li) {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0.5rem 0;
        padding: 0;
        background: none;
        border: none;

        :global(img) {
          width: 20px !important;
          height: auto !important;
          margin: 0 !important;
          display: inline-block !important;
          border-radius: 2px;
          flex-shrink: 0;
          vertical-align: middle;
        }
      }

      // Handle the regions line that has multiple flags inline
      :global(li:last-child) {
        align-items: flex-start;
        flex-wrap: wrap;

        :global(img) {
          margin-right: 0.25rem !important;
          margin-left: 0.25rem !important;
        }
      }
    }

    :global(a.copy-link) {
      color: var(--text-color1);
      text-decoration: none;
      margin-left: 0.5rem;
      opacity: 0.6;
      transition: opacity 0.3s ease;

      &:hover {
        opacity: 1;
      }
    }

    :global(blockquote) {
      background: rgba(210, 165, 82, 0.15);
      border-left: 4px solid var(--text-color1);
      padding: 1.5rem;
      margin: 2rem 0;
      border-radius: 0 8px 8px 0;
      font-style: italic;
      color: var(--text-color2);
    }

    :global(pre) {
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 1.5rem;
      margin: 2rem 0;
      overflow-x: auto;
      font-size: 0.875rem;
      line-height: 1.5;
      color: var(--text-color2);
    }

    :global(code) {
      background: rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      padding: 0.25rem 0.5rem;
      font-size: 0.875rem;
      color: var(--text-color1);
    }

    :global(table) {
      width: 100%;
      border-collapse: collapse;
      margin: 2rem 0;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      overflow: hidden;
    }

    :global(th) {
      background: rgba(210, 165, 82, 0.2);
      color: var(--text-color1);
      font-weight: 600;
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }

    // Respect markdown table alignment attributes
    :global(th[align="left"]) {
      text-align: left;
    }

    :global(th[align="center"]) {
      text-align: center;
    }

    :global(th[align="right"]) {
      text-align: right;
    }

    :global(td) {
      color: var(--text-color2);
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    :global(img) {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 2rem 0;
      display: block;
      margin-left: auto;
      margin-right: auto;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    padding: 2rem 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin-top: 1rem;

    @media (max-width: 768px) {
      padding: 1.5rem 0;
      gap: 1rem;
    }

    @media (max-width: 480px) {
      gap: 0.5rem;
      padding: 1rem 0;
    }

    @media (max-width: 360px) {
      gap: 0.25rem;
      padding: 0.75rem 0;
    }
  }

  .pagination-button {
    flex: 1;
    min-width: 0; // Allow buttons to shrink

    &.previous {
      text-align: left;
    }

    &.next {
      text-align: right;
    }
  }

  // Embedded view styles
  .embedded-document {
    min-height: 100vh;
    background: white;
    color: #333;
  }

  .embedded-content {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
    line-height: 1.6;

    :global(h1),
    :global(h2),
    :global(h3),
    :global(h4),
    :global(h5),
    :global(h6) {
      color: #333;
    }

    :global(p),
    :global(li) {
      color: #666;
    }

    :global(a) {
      color: #d2a552;
    }
  }

  @media (max-width: 320px) {
    .document-title {
      font-size: 1.5rem;
    }

    .document-article {
      padding: 1rem;
    }
  }
</style>
